import React, { useContext } from 'react';
import classnames from 'classnames';
import AppContext from 'app/AppContext';
import { usePointedTime } from 'utils/components/TimeSeriesChart/timepoint';
import { FillsGraphData } from 'widgets/fillsGraph/fillsGraphData';
import { findBucket } from 'widgets/fillsGraph/fillsGraphDataBuckets';
import { FillsChartType } from 'widgets/fillsGraph/store/configuration/FillsGraphConfiguration';
import FillsChartToggleLegend from 'widgets/fillsGraph/legend/FillsChartToggleLegend';
import { avgParticipationLabel, binParticipationLabel } from '../legend/labels';
import PercentageOfMarketVolumeHelp from './help/PercentageOfMarketVolumeHelp';
import {
    binParticipationTooltip,
    averageParticipationTooltip,
} from './help/percentageOfMarketVolumeLegendTooltips';
import { CollapseCompanionChartToggleButton } from 'widgets/fillsGraph/collapsableCompanionCharts/CollapseCompanionChartToggleButton';
import { FillsGraphPortfolioData } from 'widgets/fillsGraph/fillsGraphPortfolioData';
import { useLegendStatus } from 'widgets/fillsGraph/legendStatus';

interface PercentageOfMarketVolumeChartLegendProps {
    fillsGraphData: FillsGraphData;
    fillsGraphPortfolioData: FillsGraphPortfolioData;
    fillsGraphContainerLeft: number;
}

export default function PercentageOfMarketVolumeChartLegend(
    props: PercentageOfMarketVolumeChartLegendProps
) {
    const { fillsGraphData, fillsGraphPortfolioData, fillsGraphContainerLeft } =
        props;
    const time = usePointedTime('PercentageOfMarketVolumeChartLegend');
    const { data, timeCompleted } = fillsGraphData;
    const { numberFormat } = useContext(AppContext);

    const bucket = findBucket(data, time || timeCompleted);

    const percentOfMarketVolume = bucket
        ? bucket.percentOfMarketVolume
        : undefined;
    const averagePercentOfMarketVolume = bucket
        ? bucket.averagePercentOfMarketVolume
        : undefined;

    const formattedPercentOfMarketVolume = percentOfMarketVolume
        ? numberFormat.n2(percentOfMarketVolume)
        : undefined;

    const formattedAveragePercentOfMarketVolume = averagePercentOfMarketVolume
        ? numberFormat.n2(averagePercentOfMarketVolume)
        : undefined;

    const { allPercentageOfMarketVolumeHidden: allHidden } = useLegendStatus(
        fillsGraphData,
        fillsGraphPortfolioData
    );

    if (allHidden) {
        return <></>;
    }

    return (
        <div className={classnames('PercentageOfMarketVolumeChartLegend')}>
            <FillsChartToggleLegend
                className={`percent-of-market-volume`}
                label={binParticipationLabel}
                fillsGraphContainerLeft={fillsGraphContainerLeft}
                value={formattedPercentOfMarketVolume}
                suffix={percentOfMarketVolume ? '%' : ''}
                chart={FillsChartType.PercentageOfMarketVolume}
                property="displayPercentageOfVolume"
                hideLegendProperty={'hidePercentageOfVolumeLegend'}
                tooltip={binParticipationTooltip}
            />
            <FillsChartToggleLegend
                className={`average-percent-of-market-volume`}
                label={avgParticipationLabel}
                fillsGraphContainerLeft={fillsGraphContainerLeft}
                value={formattedAveragePercentOfMarketVolume}
                suffix={averagePercentOfMarketVolume ? '%' : ''}
                chart={FillsChartType.PercentageOfMarketVolume}
                property="displayAvgPercentageOfVolume"
                hideLegendProperty={'hideAvgPercentageOfVolumeLegend'}
                tooltip={averageParticipationTooltip}
            />
            <PercentageOfMarketVolumeHelp />
            <CollapseCompanionChartToggleButton
                section={FillsChartType.PercentageOfMarketVolume}
            />
        </div>
    );
}
